import nacl from 'tweetnacl';
import { sha256 } from 'js-sha256';

/**
 * Derives a shared symmetric secret key using Elliptic Curve Diffie-Hellman (ECDH).
 *
 * This function allows the recipient to compute the same shared secret as the sender did,
 * by combining their own private view key with the sender's ephemeral public key.
 *
 * The shared secret is used as the encryption/decryption key for a message.
 *
 * ECDH Operation:
 *   sharedPoint = receiverPrivateViewKey * ephemeralPublicKey
 *   sharedSecret = SHA-256(sharedPoint)
 *
 * @param receiverPrivateViewKey - The recipient’s 32-byte Curve25519 scalar (private view key).
 * @param ephemeralPublicKey - The 32-byte Curve25519 public key (R) sent with the message.
 * @returns A 32-byte Uint8Array representing the symmetric shared secret.
 */
export function deriveSharedSecret(
  receiverPrivateViewKey: Uint8Array,
  ephemeralPublicKey: Uint8Array
): Uint8Array {
  if (receiverPrivateViewKey.length !== 32) {
    throw new Error('receiverPrivateViewKey must be 32 bytes');
  }
  if (ephemeralPublicKey.length !== 32) {
    throw new Error('ephemeralPublicKey must be 32 bytes');
  }

  // Step 1: Perform scalar multiplication on Curve25519
  const sharedPoint = nacl.scalarMult(receiverPrivateViewKey, ephemeralPublicKey); // r * V

  // Step 2: Derive a symmetric key by hashing the curve point with SHA-256
  const sharedSecret = new Uint8Array(sha256.array(sharedPoint));

  return sharedSecret;
}